import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { gsap } from 'gsap';
import { ScreenProtectionService } from '@educational-initiatives/angular-web-utilities';
import { FreezeService } from '@educational-initiatives/angular-web-utilities';
import {
  SCREEN_PROTECTION_ANIMATION_CONSTANTS as AnimationConstants,
  SCREEN_PROTECTION_ASSETS,
  SCREEN_PROTECTION_MESSAGES,
  SCREEN_PROTECTION_CONSTANTS,
} from '@shared/constants/screen-protection.constants';

import { OverlayState } from '@shared/enums/overlay-state.enum';
import type { OverlayStateType } from '@shared/types/screen-protection.types';
import { AssetUrlPipe } from '@educational-initiatives/angular-web-utilities';

@Component({
  selector: 'app-black-screen-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AssetUrlPipe],
  templateUrl: './black-screen-overlay.component.html',
  styleUrl: './black-screen-overlay.component.scss',
})
export class BlackScreenOverlayComponent implements OnInit, OnDestroy {
  protected _isBlackScreenVisible = false;
  protected _overlayState: OverlayStateType = null;
  protected _countdown = 0;
  protected _isDissolving = false;
  protected _freezeMessage = '';

  protected readonly _overlayStateEnum = OverlayState;
  protected readonly _awayImageFilename = SCREEN_PROTECTION_ASSETS.AWAY_IMAGE;
  protected readonly _restrictedImageFilename =
    SCREEN_PROTECTION_ASSETS.RESTRICTED_IMAGE;
  protected readonly _awayTitleText = SCREEN_PROTECTION_MESSAGES.AWAY_TITLE;
  protected readonly _awaySubtitleText =
    SCREEN_PROTECTION_MESSAGES.AWAY_SUBTITLE;
  protected readonly _welcomeTitleText =
    SCREEN_PROTECTION_MESSAGES.WELCOME_TITLE;
  protected readonly _welcomeSubtitleText =
    SCREEN_PROTECTION_MESSAGES.WELCOME_SUBTITLE;
  protected readonly _devtoolsMessageText =
    SCREEN_PROTECTION_MESSAGES.DEVTOOLS_MESSAGE;
  protected readonly _devtoolsHelpText =
    SCREEN_PROTECTION_MESSAGES.DEVTOOLS_HELP;
  protected readonly _printMessageText =
    SCREEN_PROTECTION_MESSAGES.PRINT_MESSAGE;
  protected readonly _printHelpText = SCREEN_PROTECTION_MESSAGES.PRINT_HELP;

  private _subscription?: Subscription;
  private _stateSubscription?: Subscription;
  private _countdownSubscription?: Subscription;
  private _dissolvingSubscription?: Subscription;
  private _freezeSubscription?: Subscription;
  private _freezeMessageSubscription?: Subscription;
  private _mutationObserver: MutationObserver | null = null;
  private _animationTimeline: gsap.core.Timeline | null = null;

  @ViewChild('overlayContainer', { static: false })
  private readonly _overlayContainer?: ElementRef<HTMLElement>;
  @ViewChild('countdownNumber', { static: false })
  private readonly _countdownNumber?: ElementRef<HTMLElement>;
  @ViewChild('awayContent', { static: false })
  private readonly _awayContent?: ElementRef<HTMLElement>;
  @ViewChild('awayImage', { static: false })
  private readonly _awayImage?: ElementRef<HTMLElement>;
  @ViewChild('awayTitle', { static: false })
  private readonly _awayTitle?: ElementRef<HTMLElement>;
  @ViewChild('awaySubtitle', { static: false })
  private readonly _awaySubtitle?: ElementRef<HTMLElement>;
  @ViewChild('awayDots', { static: false })
  private readonly _awayDots?: ElementRef<HTMLElement>;
  @ViewChild('welcomeContent', { static: false })
  private readonly _welcomeContent?: ElementRef<HTMLElement>;
  @ViewChild('welcomeTitle', { static: false })
  private readonly _welcomeTitle?: ElementRef<HTMLElement>;
  @ViewChild('welcomeSubtitle', { static: false })
  private readonly _welcomeSubtitle?: ElementRef<HTMLElement>;
  @ViewChild('restrictedContent', { static: false })
  private readonly _restrictedContent?: ElementRef<HTMLElement>;
  @ViewChild('restrictedImage', { static: false })
  private readonly _restrictedImage?: ElementRef<HTMLElement>;
  @ViewChild('warningMessage', { static: false })
  private readonly _warningMessage?: ElementRef<HTMLElement>;
  @ViewChild('helpText', { static: false })
  private readonly _helpText?: ElementRef<HTMLElement>;

  constructor(
    private readonly _screenProtectionService: ScreenProtectionService,
    private readonly _freezeService: FreezeService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _elementRef: ElementRef<HTMLElement>,
  ) {}

  public ngOnInit(): void {
    this._subscription =
      this._screenProtectionService.shouldShowBlackScreen.subscribe(
        (shouldShow: boolean) => {
          this._isBlackScreenVisible = shouldShow;
          this._cdr.detectChanges();
        },
      );

    this._stateSubscription =
      this._screenProtectionService.overlayState.subscribe(
        (state: OverlayStateType) => {
          this._overlayState = state;
          this._cdr.detectChanges();
          setTimeout(
            () => this.animateStateChange(),
            AnimationConstants.ANIMATION_TRIGGER_DELAY,
          );
        },
      );

    this._countdownSubscription =
      this._screenProtectionService.countdown.subscribe((count: number) => {
        this._countdown = count;
        this._cdr.detectChanges();
        if (this._overlayState === OverlayState.COUNTDOWN) {
          setTimeout(
            () => this.animateCountdown(),
            AnimationConstants.ANIMATION_TRIGGER_DELAY,
          );
        }
      });

    this._dissolvingSubscription =
      this._screenProtectionService.isDissolving.subscribe(
        (isDissolving: boolean) => {
          this._isDissolving = isDissolving;
          if (isDissolving) {
            this.animateDissolve();
          }
          this._cdr.detectChanges();
        },
      );

    // Subscribe to freeze service
    this._freezeSubscription = this._freezeService.isFrozen.subscribe(
      (isFrozen: boolean) => {
        if (isFrozen) {
          this._overlayState = OverlayState.FREEZE;
          this._isBlackScreenVisible = true;
        } else if (this._overlayState === OverlayState.FREEZE) {
          this._overlayState = null;
          this._isBlackScreenVisible = false;
        }
        this._cdr.detectChanges();
        if (isFrozen) {
          setTimeout(
            () => this.animateStateChange(),
            AnimationConstants.ANIMATION_TRIGGER_DELAY,
          );
        }
      },
    );

    this._freezeMessageSubscription =
      this._freezeService.freezeMessage.subscribe((message: string) => {
        this._freezeMessage = message;
        this._cdr.detectChanges();
      });

    this.monitorSelfRemoval();
  }

  /**
   * Animates state changes based on current overlay state
   */
  /* v8 ignore start - GSAP animation code */
  private animateStateChange(): void {
    if (!this._overlayContainer?.nativeElement) {
      return;
    }

    if (this._animationTimeline) {
      this._animationTimeline.kill();
    }

    switch (this._overlayState) {
      case OverlayState.COUNTDOWN:
        this.animateCountdown();
        break;
      case OverlayState.AWAY:
        this.animateAway();
        break;
      case OverlayState.WELCOME:
        this.animateWelcome();
        break;
      case OverlayState.DEVTOOLS:
      case OverlayState.PRINT:
        this.animateRestricted();
        break;
      case OverlayState.FREEZE:
        this.animateRestricted();
        break;
    }
  }

  /**
   * Animates the countdown number with elastic effect
   */
  private animateCountdown(): void {
    if (!this._countdownNumber?.nativeElement) {
      return;
    }

    const element = this._countdownNumber.nativeElement;
    gsap.killTweensOf(element);

    gsap.fromTo(
      element,
      {
        scale: AnimationConstants.COUNTDOWN_INITIAL_SCALE,
        opacity: 0,
        rotation: AnimationConstants.COUNTDOWN_INITIAL_ROTATION,
        y: AnimationConstants.COUNTDOWN_INITIAL_Y,
        filter: AnimationConstants.COUNTDOWN_BLUR,
      },
      {
        scale: AnimationConstants.COUNTDOWN_FINAL_SCALE,
        opacity: 1,
        rotation: 0,
        y: 0,
        filter: 'blur(0px)',
        duration: AnimationConstants.COUNTDOWN_DURATION,
        ease: 'elastic.out(1, 0.3)',
        onComplete: () => {
          gsap.to(element, {
            scale: AnimationConstants.COUNTDOWN_NORMAL_SCALE,
            duration: AnimationConstants.COUNTDOWN_PULSE_DURATION,
            ease: 'power2.out',
          });
        },
      },
    );

    gsap.to(element, {
      scale: AnimationConstants.COUNTDOWN_PULSE_SCALE,
      duration: AnimationConstants.COUNTDOWN_PULSE_DURATION,
      yoyo: true,
      repeat: 1,
      ease: 'power2.inOut',
      delay: AnimationConstants.COUNTDOWN_PULSE_DELAY,
    });
  }

  /**
   * Animates the away state with image, title, subtitle, and dots
   */
  private animateAway(): void {
    if (!this._awayContent?.nativeElement) {
      return;
    }

    const content = this._awayContent.nativeElement;
    const image = content.querySelector('.away-image') as HTMLElement;
    const title = content.querySelector('.away-title') as HTMLElement;
    const subtitle = content.querySelector('.away-subtitle') as HTMLElement;
    const dots = content.querySelectorAll('.dot') as NodeListOf<HTMLElement>;

    if (this._animationTimeline) {
      this._animationTimeline.kill();
    }
    this._animationTimeline = gsap.timeline();

    if (image) {
      const timeline = this._animationTimeline;
      if (timeline) {
        timeline.fromTo(
          image,
          {
            scale: AnimationConstants.AWAY_IMAGE_INITIAL_SCALE,
            opacity: 0,
            y: AnimationConstants.AWAY_IMAGE_INITIAL_Y,
            rotation: AnimationConstants.AWAY_IMAGE_INITIAL_ROTATION,
            filter: AnimationConstants.AWAY_IMAGE_BLUR,
          },
          {
            scale: AnimationConstants.AWAY_IMAGE_FINAL_SCALE,
            opacity: 1,
            y: 0,
            rotation: 0,
            filter: 'blur(0px)',
            duration: AnimationConstants.AWAY_IMAGE_DURATION,
            ease: 'elastic.out(1, 0.5)',
          },
        );
      }

      gsap.to(image, {
        y: AnimationConstants.AWAY_IMAGE_FLOAT_Y,
        duration: AnimationConstants.AWAY_IMAGE_FLOAT_DURATION,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay: AnimationConstants.AWAY_IMAGE_FLOAT_DELAY,
      });
    }

    if (title) {
      this.animateTitleText(title);
    }

    if (subtitle) {
      const timelineForSubtitle = this._animationTimeline;
      if (!timelineForSubtitle) {
        return;
      }
      timelineForSubtitle.fromTo(
        subtitle,
        {
          opacity: 0,
          y: AnimationConstants.AWAY_SUBTITLE_INITIAL_Y,
          scale: AnimationConstants.AWAY_SUBTITLE_INITIAL_SCALE,
          filter: AnimationConstants.AWAY_SUBTITLE_BLUR,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: AnimationConstants.AWAY_SUBTITLE_DURATION,
          ease: 'power3.out',
        },
        AnimationConstants.AWAY_SUBTITLE_OFFSET,
      );
    }

    if (dots.length > 0) {
      this.animateAwayDots(dots);
    }
  }

  /**
   * Animates title text character by character
   */
  private animateTitleText(title: HTMLElement): void {
    const text = title.textContent || '';
    title.textContent = '';
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }
    timeline.set(title, { opacity: 1 });

    const chars = text.split('').map((char: string) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      span.style.opacity = '0';
      span.style.transform = `translateY(${AnimationConstants.AWAY_TITLE_CHAR_INITIAL_Y}px) rotateX(90deg)`;
      return span;
    });

    chars.forEach((span: HTMLSpanElement) => title.appendChild(span));

    timeline.to(
      chars,
      {
        opacity: 1,
        y: 0,
        rotationX: 0,
        duration: AnimationConstants.AWAY_TITLE_CHAR_DURATION,
        stagger: AnimationConstants.AWAY_TITLE_CHAR_STAGGER,
        ease: 'back.out(1.7)',
      },
      AnimationConstants.AWAY_SUBTITLE_OFFSET,
    );
  }

  /**
   * Animates away dots with bounce effect
   */
  private animateAwayDots(dots: NodeListOf<HTMLElement>): void {
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }

    timeline.fromTo(
      dots,
      {
        scale: AnimationConstants.AWAY_DOT_INITIAL_SCALE,
        opacity: 0,
        y: AnimationConstants.AWAY_DOT_INITIAL_Y,
        rotation: AnimationConstants.AWAY_DOT_INITIAL_ROTATION,
      },
      {
        scale: 1,
        opacity: AnimationConstants.AWAY_DOT_OPACITY,
        y: 0,
        rotation: 0,
        duration: AnimationConstants.AWAY_DOT_DURATION,
        stagger: AnimationConstants.AWAY_DOT_STAGGER,
        ease: 'elastic.out(1, 0.5)',
      },
      AnimationConstants.AWAY_DOT_OFFSET,
    );

    dots.forEach((dot: HTMLElement, index: number) => {
      gsap.to(dot, {
        y: AnimationConstants.AWAY_DOT_BOUNCE_Y,
        scale: AnimationConstants.AWAY_DOT_BOUNCE_SCALE,
        duration: AnimationConstants.AWAY_DOT_BOUNCE_DURATION,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        delay:
          index * AnimationConstants.AWAY_DOT_BOUNCE_DELAY_INCREMENT +
          AnimationConstants.AWAY_DOT_BOUNCE_DELAY_BASE,
      });
    });
  }

  /**
   * Animates the welcome back state
   */
  private animateWelcome(): void {
    if (!this._welcomeContent?.nativeElement) {
      return;
    }

    const content = this._welcomeContent.nativeElement;
    const title = content.querySelector('.welcome-title') as HTMLElement;
    const subtitle = content.querySelector('.welcome-subtitle') as HTMLElement;

    if (this._animationTimeline) {
      this._animationTimeline.kill();
    }
    this._animationTimeline = gsap.timeline();

    if (title) {
      this.animateWelcomeTitle(title);
    }

    if (subtitle) {
      this.animateWelcomeSubtitle(subtitle);
    }
  }

  /**
   * Animates welcome title word by word
   */
  private animateWelcomeTitle(title: HTMLElement): void {
    const text = title.textContent || '';
    title.textContent = '';
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }
    timeline.set(title, { opacity: 1 });

    const words = text.split(' ').map((word: string) => {
      const span = document.createElement('span');
      span.textContent = word;
      span.style.display = 'inline-block';
      span.style.marginRight = '0.3em';
      span.style.opacity = '0';
      span.style.transform = `translateY(${AnimationConstants.WELCOME_WORD_INITIAL_Y}px) scale(${AnimationConstants.WELCOME_WORD_INITIAL_SCALE})`;
      return span;
    });

    words.forEach((span: HTMLSpanElement) => title.appendChild(span));

    timeline.to(words, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: AnimationConstants.WELCOME_WORD_DURATION,
      stagger: AnimationConstants.WELCOME_WORD_STAGGER,
      ease: 'back.out(2)',
    });

    timeline.to(
      words,
      {
        y: AnimationConstants.WELCOME_WORD_BOUNCE_Y,
        duration: AnimationConstants.WELCOME_WORD_BOUNCE_DURATION,
        stagger: AnimationConstants.WELCOME_WORD_BOUNCE_STAGGER,
        ease: 'power2.out',
        yoyo: true,
        repeat: 1,
      },
      AnimationConstants.WELCOME_WORD_BOUNCE_OFFSET,
    );
  }

  /**
   * Animates welcome subtitle character by character
   */
  private animateWelcomeSubtitle(subtitle: HTMLElement): void {
    const originalText = subtitle.textContent || '';
    subtitle.textContent = '';
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }
    timeline.set(subtitle, { opacity: 1 });

    const chars = originalText.split('').map((char: string) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      span.style.opacity = '0';
      return span;
    });

    chars.forEach((span: HTMLSpanElement) => subtitle.appendChild(span));

    timeline.to(
      chars,
      {
        opacity: 1,
        duration: AnimationConstants.WELCOME_CHAR_DURATION,
        stagger: AnimationConstants.WELCOME_CHAR_STAGGER,
        ease: 'none',
      },
      AnimationConstants.WELCOME_CHAR_OFFSET,
    );
  }

  /**
   * Animates the restricted state (devtools or print)
   */
  private animateRestricted(): void {
    if (!this._restrictedContent?.nativeElement) {
      return;
    }

    const content = this._restrictedContent.nativeElement;
    const image = content.querySelector('.restricted-image') as HTMLElement;
    const message = content.querySelector('.warning-message') as HTMLElement;
    const helpText = content.querySelector('.help-text') as HTMLElement;

    if (this._animationTimeline) {
      this._animationTimeline.kill();
    }
    this._animationTimeline = gsap.timeline();

    if (image) {
      this.animateRestrictedImage(image);
    }

    if (message) {
      this.animateRestrictedMessage(message);
    }

    if (helpText) {
      const timelineForHelp = this._animationTimeline;
      if (!timelineForHelp) {
        return;
      }
      timelineForHelp.fromTo(
        helpText,
        {
          opacity: 0,
          y: AnimationConstants.RESTRICTED_HELP_INITIAL_Y,
          scale: AnimationConstants.RESTRICTED_HELP_INITIAL_SCALE,
          filter: AnimationConstants.RESTRICTED_HELP_BLUR,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: AnimationConstants.RESTRICTED_HELP_DURATION,
          ease: 'back.out(1.7)',
        },
        AnimationConstants.RESTRICTED_HELP_OFFSET,
      );
    }
  }

  /**
   * Animates restricted image with shake and pulse effects
   */
  private animateRestrictedImage(image: HTMLElement): void {
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }

    timeline.fromTo(
      image,
      {
        scale: AnimationConstants.RESTRICTED_IMAGE_INITIAL_SCALE,
        opacity: 0,
        rotation: AnimationConstants.RESTRICTED_IMAGE_INITIAL_ROTATION,
        filter: AnimationConstants.RESTRICTED_IMAGE_BLUR,
      },
      {
        scale: AnimationConstants.RESTRICTED_IMAGE_FINAL_SCALE,
        opacity: 1,
        rotation: 0,
        filter: 'blur(0px) brightness(1)',
        duration: AnimationConstants.RESTRICTED_IMAGE_DURATION,
        ease: 'elastic.out(1, 0.4)',
      },
    );

    timeline.to(
      image,
      {
        x: AnimationConstants.RESTRICTED_IMAGE_SHAKE_X,
        duration: AnimationConstants.RESTRICTED_IMAGE_SHAKE_DURATION,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: AnimationConstants.RESTRICTED_IMAGE_SHAKE_REPEAT,
      },
      AnimationConstants.RESTRICTED_IMAGE_SHAKE_OFFSET,
    );

    gsap.to(image, {
      scale: AnimationConstants.RESTRICTED_IMAGE_PULSE_SCALE,
      duration: AnimationConstants.RESTRICTED_IMAGE_PULSE_DURATION,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      delay: AnimationConstants.RESTRICTED_IMAGE_PULSE_DELAY,
    });
  }

  /**
   * Animates restricted message with shake effect
   */
  private animateRestrictedMessage(message: HTMLElement): void {
    const timeline = this._animationTimeline;
    if (!timeline) {
      return;
    }

    timeline.fromTo(
      message,
      {
        opacity: 0,
        x: AnimationConstants.RESTRICTED_MESSAGE_INITIAL_X,
        scale: AnimationConstants.RESTRICTED_MESSAGE_INITIAL_SCALE,
        filter: AnimationConstants.RESTRICTED_MESSAGE_BLUR,
      },
      {
        opacity: 1,
        x: 0,
        scale: 1,
        filter: 'blur(0px)',
        duration: AnimationConstants.RESTRICTED_MESSAGE_DURATION,
        ease: 'power4.out',
      },
      AnimationConstants.RESTRICTED_MESSAGE_OFFSET,
    );

    timeline.to(
      message,
      {
        x: AnimationConstants.RESTRICTED_MESSAGE_SHAKE_X,
        duration: AnimationConstants.RESTRICTED_MESSAGE_SHAKE_DURATION,
        ease: 'power2.inOut',
        yoyo: true,
        repeat: AnimationConstants.RESTRICTED_MESSAGE_SHAKE_REPEAT,
      },
      AnimationConstants.RESTRICTED_MESSAGE_SHAKE_OFFSET,
    );
  }

  /**
   * Animates the dissolving effect when returning from away state
   */
  private animateDissolve(): void {
    if (!this._overlayContainer?.nativeElement) {
      return;
    }

    const overlay = this._overlayContainer.nativeElement;
    const content = overlay.querySelector(
      '.black-screen-content',
    ) as HTMLElement;
    const dissolveTimeline = gsap.timeline();

    if (content) {
      dissolveTimeline.to(content, {
        scale: AnimationConstants.DISSOLVE_CONTENT_SCALE,
        opacity: 0,
        rotation: AnimationConstants.DISSOLVE_CONTENT_ROTATION,
        filter: AnimationConstants.DISSOLVE_CONTENT_BLUR,
        duration: AnimationConstants.DISSOLVE_CONTENT_DURATION,
        ease: 'power3.in',
      });
    }

    dissolveTimeline.to(
      overlay,
      {
        opacity: 0,
        scale: AnimationConstants.DISSOLVE_OVERLAY_SCALE,
        filter: AnimationConstants.DISSOLVE_OVERLAY_BLUR,
        duration: AnimationConstants.DISSOLVE_OVERLAY_DURATION,
        ease: 'power4.inOut',
      },
      AnimationConstants.DISSOLVE_OVERLAY_OFFSET,
    );

    dissolveTimeline.set(overlay, {
      visibility: 'hidden',
    });
  }

  /**
   * Monitors and protects against DOM removal of the overlay using MutationObserver
   */
  private monitorSelfRemoval(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    const componentElement = this._elementRef.nativeElement;
    if (!componentElement) {
      return;
    }

    this._mutationObserver = new MutationObserver(mutations => {
      if (!this._isBlackScreenVisible) {
        return;
      }

      if (!componentElement.parentNode) {
        this.reattachOverlay();
        return;
      }

      const overlayDiv = componentElement.querySelector(
        '.black-screen-overlay',
      );
      if (!overlayDiv && this._isBlackScreenVisible) {
        this.reattachOverlay();
        return;
      }

      mutations.forEach(mutation => {
        mutation.removedNodes.forEach(node => {
          if (
            node === overlayDiv ||
            (node.nodeType === Node.ELEMENT_NODE &&
              (node as Element).classList.contains('black-screen-overlay'))
          ) {
            if (this._isBlackScreenVisible) {
              this.reattachOverlay();
            }
          }
        });
      });
    });

    const parentNode = componentElement.parentNode || document.body;
    this._mutationObserver.observe(parentNode, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Reattaches the overlay if it was removed from DOM
   */
  private reattachOverlay(): void {
    this._isBlackScreenVisible = false;
    this._cdr.detectChanges();
    setTimeout(() => {
      this._isBlackScreenVisible = true;
      this._cdr.detectChanges();
    }, SCREEN_PROTECTION_CONSTANTS.SELF_PROTECTION_REATTACH_DELAY_MS);
  }

  private cleanupAnimations(): void {
    if (this._animationTimeline) {
      this._animationTimeline.kill();
      this._animationTimeline = null;
    }

    if (this._overlayContainer?.nativeElement) {
      gsap.killTweensOf(this._overlayContainer.nativeElement);
      const content = this._overlayContainer.nativeElement.querySelector(
        '.black-screen-content',
      );
      if (content) {
        gsap.killTweensOf(content);
      }
    }

    if (this._countdownNumber?.nativeElement) {
      gsap.killTweensOf(this._countdownNumber.nativeElement);
    }

    if (this._awayContent?.nativeElement) {
      const content = this._awayContent.nativeElement;
      gsap.killTweensOf(content);

      const image = content.querySelector('.away-image');
      const title = content.querySelector('.away-title');
      const subtitle = content.querySelector('.away-subtitle');
      const dots = content.querySelectorAll('.dot');

      if (image) {
        gsap.killTweensOf(image);
      }
      if (title) {
        gsap.killTweensOf(title);
        const titleSpans = title.querySelectorAll('span');
        if (titleSpans.length > 0) {
          gsap.killTweensOf(titleSpans);
        }
      }
      if (subtitle) {
        gsap.killTweensOf(subtitle);
      }
      if (dots.length > 0) {
        gsap.killTweensOf(dots);
      }
    }

    if (this._welcomeContent?.nativeElement) {
      const content = this._welcomeContent.nativeElement;
      gsap.killTweensOf(content);

      const title = content.querySelector('.welcome-title');
      const subtitle = content.querySelector('.welcome-subtitle');

      if (title) {
        gsap.killTweensOf(title);
        const titleSpans = title.querySelectorAll('span');
        if (titleSpans.length > 0) {
          gsap.killTweensOf(titleSpans);
        }
      }
      if (subtitle) {
        gsap.killTweensOf(subtitle);
        const subtitleSpans = subtitle.querySelectorAll('span');
        if (subtitleSpans.length > 0) {
          gsap.killTweensOf(subtitleSpans);
        }
      }
    }

    if (this._restrictedContent?.nativeElement) {
      const content = this._restrictedContent.nativeElement;
      gsap.killTweensOf(content);

      const image = content.querySelector('.restricted-image');
      const message = content.querySelector('.warning-message');
      const helpText = content.querySelector('.help-text');

      if (image) {
        gsap.killTweensOf(image);
      }
      if (message) {
        gsap.killTweensOf(message);
      }
      if (helpText) {
        gsap.killTweensOf(helpText);
      }
    }
  }

  public ngOnDestroy(): void {
    this.cleanupAnimations();

    if (this._subscription) {
      this._subscription.unsubscribe();
    }

    if (this._stateSubscription) {
      this._stateSubscription.unsubscribe();
    }

    if (this._countdownSubscription) {
      this._countdownSubscription.unsubscribe();
    }

    if (this._dissolvingSubscription) {
      this._dissolvingSubscription.unsubscribe();
    }

    if (this._freezeSubscription) {
      this._freezeSubscription.unsubscribe();
    }

    if (this._freezeMessageSubscription) {
      this._freezeMessageSubscription.unsubscribe();
    }

    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
  }
  /* v8 ignore stop */
}
